import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import PropertyForm from '../components/PropertyForm';
import Reveal from '../components/Reveal';
import { Trash2, LogOut, Edit } from 'lucide-react';

const AdminDashboard = () => {
  const { logout } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProperty, setEditingProperty] = useState(null);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'properties'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const props = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProperties(props);
    } catch (error) {
      console.error("Error fetching properties: ", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        await deleteDoc(doc(db, 'properties', id));
        fetchProperties(); // Refresh list
      } catch (error) {
        console.error("Error deleting property: ", error);
        alert('Failed to delete property');
      }
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingProperty(null);
  };

  const handlePropertySaved = () => {
    setEditingProperty(null);
    fetchProperties();
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  return (
    <div className="admin-dashboard container" style={{ padding: '120px 20px 50px' }}>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <h1>Admin Dashboard</h1>
        <button className="btn btn-outline-danger" onClick={handleLogout}>
          <LogOut size={16} style={{ marginRight: '8px' }} /> Log Out
        </button>
      </div>

      <div className="row">
        <div className="col-lg-4 mb-5">
          <PropertyForm 
            onPropertyAdded={handlePropertySaved} 
            propertyToEdit={editingProperty}
            onCancel={handleCancelEdit}
          />
        </div>

        <div className="col-lg-8">
          <Reveal>
            <div className="properties-list-admin" style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
              <h3 className="mb-4">Property Listings</h3>
              {loading ? <p>Loading properties...</p> : (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Title</th>
                        <th>Price</th>
                        <th>Tag</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {properties.map(property => (
                        <tr key={property.id}>
                          <td>
                            <img src={property.image} alt={property.title} style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                          </td>
                          <td>{property.title}</td>
                          <td>{property.price ? `₦${property.price}` : 'N/A'}</td>
                          <td>
                            <span className={`badge ${property.tag === 'For Sale' ? 'bg-success' : 'bg-secondary'}`} style={{ padding: '5px 10px', borderRadius: '12px', fontSize: '0.8rem', color: 'white', backgroundColor: property.tag === 'For Sale' ? '#28a745' : '#6c757d' }}>
                              {property.tag}
                            </span>
                          </td>
                          <td>
                            <button className="btn btn-sm btn-primary me-2" onClick={() => handleEdit(property)} style={{ marginRight: '8px' }}>
                              <Edit size={16} />
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(property.id)}>
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {properties.length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center">No properties found. Add one!</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
